// Signature verification for directory material.
//
// Live directory keys are ES256 (P-256, SEC1 uncompressed, 65 bytes) — WebCrypto
// verifies those natively everywhere. `common.yaml` also permits Ed25519 for
// directory keys (they live outside a phone); that branch is dormant against the
// live directory but implemented: raw 32-byte keys, raw 64-byte signatures,
// feature-detected at import time. If an engine without WebCrypto Ed25519 ever
// meets an Ed25519 directory key, the recorded contingency is @noble/ed25519
// behind this same function — not added now because it would be dead code.

import { b64uDecodeOrNull } from './base64url';
import { p1363FromDer } from './der';

const keyCache = new Map<string, Promise<CryptoKey | null>>();

function importVerifyKey(keyB64: string): Promise<CryptoKey | null> {
  let cached = keyCache.get(keyB64);
  if (!cached) {
    cached = importFresh(keyB64);
    keyCache.set(keyB64, cached);
  }
  return cached;
}

async function importFresh(keyB64: string): Promise<CryptoKey | null> {
  const bytes = b64uDecodeOrNull(keyB64);
  if (bytes === null) {
    return null;
  }
  try {
    if (bytes.length === 65 && bytes[0] === 0x04) {
      // WebCrypto validates that the point is on the curve during import.
      return await crypto.subtle.importKey(
        'raw',
        bytes as BufferSource,
        { name: 'ECDSA', namedCurve: 'P-256' },
        false,
        ['verify'],
      );
    }
    if (bytes.length === 32) {
      return await crypto.subtle.importKey('raw', bytes as BufferSource, 'Ed25519', false, [
        'verify',
      ]);
    }
  } catch {
    // Not a valid key, or the engine lacks the algorithm — either way: no key.
  }
  return null;
}

/** Whether [signatureB64] is a valid signature over [material] by [keyB64]. */
export async function verifyWithKey(
  keyB64: string,
  material: Uint8Array,
  signatureB64: string,
): Promise<boolean> {
  const key = await importVerifyKey(keyB64);
  const signature = b64uDecodeOrNull(signatureB64);
  if (key === null || signature === null) {
    return false;
  }
  try {
    if (key.algorithm.name === 'ECDSA') {
      const raw = p1363FromDer(signature);
      if (raw === null) {
        return false;
      }
      return await crypto.subtle.verify(
        { name: 'ECDSA', hash: 'SHA-256' },
        key,
        raw as BufferSource,
        material as BufferSource,
      );
    }
    return await crypto.subtle.verify('Ed25519', key, signature as BufferSource, material as BufferSource);
  } catch {
    return false;
  }
}
